/**
 * Governance config resolution (Step 4 Phase 4.3) — per-runtime canary wiring.
 *
 * Each runtime mount calls `resolveGovernanceConfig()` and passes the result to
 * `createAppRuntimeCore({ governance })`. The mode comes from the `COGO_RUNTIME_GOVERNANCE` env var
 * (off | audit | enforce, default off) so a single runtime can be flipped to `audit` in a canary
 * without a code change. Runtimes without `process.env` (Flutter-embed, React Native) pass an
 * explicit `mode` override instead. The contract itself is NOT set here — the Core reads it from
 * `manifest.meta.stateContract` (Phase 4.2).
 *
 * See docs/agentic/runtime/step4-checkwrite-governance-design.md §3c, §5 (4.3).
 */
import type { GovernanceMode } from "./state-manager"
import type { AuditEntry } from "./security/audit-log"

export interface GovernanceConfig {
  mode: GovernanceMode
  onViolation?: (entry: AuditEntry) => void
}

/** Read `COGO_RUNTIME_GOVERNANCE` (multi-runtime safe — guards `typeof process`). Unknown/unset → 'off'. */
export function readEnvGovernanceMode(): GovernanceMode {
  const raw =
    typeof process !== "undefined"
      ? process.env?.["COGO_RUNTIME_GOVERNANCE"]?.toLowerCase()
      : undefined
  return raw === "enforce" ? "enforce" : raw === "audit" ? "audit" : "off"
}

/**
 * Default audit report sink — dedupes by `operation:path` and warns once per unique violation, so a
 * canary surfaces what a generated app WOULD be denied without flooding the console.
 */
export function defaultGovernanceSink(): (entry: AuditEntry) => void {
  const seen = new Set<string>()
  return (entry) => {
    const key = `${entry.operation}:${entry.path}`
    if (seen.has(key)) return
    seen.add(key)
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn(
        `[cogo:governance] ${entry.operation} "${entry.path}" not declared in stateContract (${entry.reason})`,
      )
    }
  }
}

/**
 * Resolve the governance config for a Core mount. Precedence: `override.mode` → env → 'off'.
 * Returns `undefined` for 'off' so the Core keeps its zero-overhead default (no contract consulted).
 * For audit/enforce it attaches the dedup sink unless an explicit `onViolation` is supplied.
 */
export function resolveGovernanceConfig(override?: {
  mode?: GovernanceMode
  onViolation?: (entry: AuditEntry) => void
}): GovernanceConfig | undefined {
  const mode = override?.mode ?? readEnvGovernanceMode()
  if (mode === "off") return undefined
  return { mode, onViolation: override?.onViolation ?? defaultGovernanceSink() }
}
